<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { STATUS_CLOSED, STATUS_OPEN, AIRPORT, REGION, SUB_REGION } from '@/constants';
import { useViewComposable } from '@/views/composable';
import { FilterMatchMode, FilterOperator, FilterService } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import capitalize from 'lodash/capitalize';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import ActionButtons from '@/components/ActionButtons.vue';
import ConfirmDeleteModal from '@/components/modal/ConfirmDeleteModal.vue';

const props = defineProps<{
  tourGatewayDestData: any[];
  tourGatewayDestDataDefault?: any[];
  isView?: boolean;
  urlUpload?: string;
}>();

const emits = defineEmits<{
  handleRemoveTourGateway: [data: any];
  openEditTourModal: [data: any];
  handlePreviewFile: [data: any];
  handleShowRevertVersion: [];
}>();

const { t } = useI18n({ useScope: 'global' });
const { styleColumnExport, formatDataExport, renderNameFileExport, formatColumnExport } = useViewComposable();
const widthList = [10, 10, 40, 40, 20, 10, 10, 40, 40, 20, 10, 40, 40, 40, 40, 40, 30, 15, 30, 30, 30, 30, 10, 10];
const sheetColumnStyle = styleColumnExport(widthList);
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;

const currentLanguage = getCurrentLocale();
const capitalizeLang = capitalize(currentLanguage);
const dialog = useDialog();

const sheetsData = ref<any>([]);
const dataTableTourRef = ref();
const deletedStatuses = ref([
  { name: t('management.tour.deleted').toUpperCase(), value: true },
  { name: t('management.tour.none').toUpperCase(), value: false }
]);
const referenceTypeList = ref([
  {
    name: t('common.airport'),
    value: AIRPORT
  },
  {
    name: t('common.region'),
    value: REGION
  },
  {
    name: t('common.sub_region'),
    value: SUB_REGION
  }
]);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const rowsPerPageOptions = ref([10, 25, 50, 100, 200, 400, 500]);
const filtersTour = ref();
const sortOrder = ref(0);
const sortField = ref('validateMessage');
const isSort = ref(false);

FilterService.register('filterDates', (dates, filterValue) => {
  const datesFormat = handleDisplayDate(dates, ' ');
  return !filterValue || datesFormat.includes(filterValue);
});

FilterService.register('filterDurations', (typeOption, filterValue) => {
  const strArray = filterValue?.replace(/\s+/g, '').split(',');
  const joinValue = typeOption?.join(',');
  return !filterValue || strArray.every((value: any) => joinValue.includes(value));
});

const initFilters = () => {
  filtersTour.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    gateCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    gateId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`gateName${capitalizeLang}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    gateReferenceType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    destId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`destName${capitalizeLang}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destReferenceType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    parentTourId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`tourName${capitalizeLang}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    quoteDate: { value: null, matchMode: 'filterDates' },
    travelDate: { value: null, matchMode: 'filterDates' },
    blackoutDate: { value: null, matchMode: 'filterDates' },
    durations: { value: null, matchMode: 'filterDurations' },
    parentDestId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    combinationStatus: { operator: FilterOperator.OR, constraints: [{ value: props.isView ? null : STATUS_OPEN, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`gateCountry${capitalizeLang}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`destCountry${capitalizeLang}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    typeInput: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    deleteStatus: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] }
  };
};

const columnFields = ref([
  { field: 'gateCode', header: t('management.package.gateway_code') },
  { field: 'gateId', header: t('management.tour.gateway_id') },
  { field: `gateName${capitalizeLang}`, header: t('management.package.gateway_name'), fieldOther: getTextVariant(currentLanguage, 'gateNameFr', 'gateNameEn') },
  { field: 'gateReferenceType', header: t('management.tour.gate_type') },
  { field: 'destCode', header: t('management.package.destination_code') },
  { field: 'destId', header: t('management.package.destination_id') },
  { field: `destName${capitalizeLang}`, header: t('management.package.destination_name'), fieldOther: getTextVariant(currentLanguage, 'destNameFr', 'destNameEn') },
  { field: 'destReferenceType', header: t('management.tour.dest_type') },
  { field: 'parentTourId', header: t('management.tour.parent_tour_id') },
  { field: `tourName${capitalizeLang}`, header: t('management.tour.tour_name'), fieldOther: getTextVariant(currentLanguage, 'tourNameFr', 'tourNameEn') },
  { field: 'quoteDate', header: t('common.labels.quote_date') },
  { field: 'travelDate', header: t('hotel_contract.travel_date_range') },
  { field: 'blackoutDate', header: t('hotel_contract.black_out_date') },
  { field: 'durations', header: t('common.labels.durations') },
  { field: 'parentDestId', header: t('management.tour.parent_dest_id') },
  { field: `gateCountry${capitalizeLang}`, header: t('management.tour.gate_country'), fieldOther: getTextVariant(currentLanguage, 'gateCountryFr', 'gateCountryFr') },
  { field: `destCountry${capitalizeLang}`, header: t('management.tour.dest_country'), fieldOther: getTextVariant(currentLanguage, 'destCountryFr', 'destCountryFr') },
  { field: 'typeInput', header: t('management.tour.input_type') },
  { field: 'combinationStatus', header: t('common.status') },
  { field: 'deleteStatus', header: t('management.tour.deleted_status') },
  { field: 'action', header: t('common.action') }
]);

const formatShowFieldColumn = (isDisplayColumn?: boolean) => {
  const filterColumn = formatColumnExport(columnFields.value, currentLanguage);
  const tourOnlyColumn = { field: 'isTourOnly', header: t('management.tour.tour_only') };
  const messageValidation = { field: 'validateMessage', header: t('management.hotel.message') };
  return !isDisplayColumn ? [...filterColumn, tourOnlyColumn] : [...filterColumn, tourOnlyColumn, messageValidation];
};

const columns = ref(props.isView ? formatShowFieldColumn(true) : columnFields.value);

const globalFilterFields = ref([
  'gateCode',
  'gateId',
  `gateName${capitalizeLang}`,
  'gateReferenceType',
  'destCode',
  'destId',
  `destName${capitalizeLang}`,
  'destReferenceType',
  'parentTourId',
  `tourName${capitalizeLang}`,
  'quoteDate',
  'travelDate',
  'blackoutDate',
  'durations',
  'parentDestId',
  `gateCountry${capitalizeLang}`,
  `destCountry${capitalizeLang}`,
  'typeInput',
  'combinationStatus',
  'deleteStatus'
]);

initFilters();

const clearFilter = () => {
  initFilters();
};

const deleteTour = (tourData: any) => {
  const deletePayload = {
    sk: tourData.sk,
    pk: tourData.pk
  };
  const dialogRef = dialog.open(ConfirmDeleteModal, {
    props: {
      header: t('common.confirm_delete'),
      modal: true
    },
    data: {
      title: tourData.deleteStatus ? t('management.tour.record_deleted_permanently') : t('notification.confirm_delete_message')
    },
    emits: {
      onSave: () => {
        emits('handleRemoveTourGateway', deletePayload);
        dialogRef.close();
      }
    }
  });
};

const getDeleteSeverity = (status: string) => {
  if (!status) {
    return 'info';
  } else {
    return 'danger';
  }
};

const getSeverity = (status: string) => {
  switch (status) {
    case STATUS_OPEN:
      return 'info';
    case STATUS_CLOSED:
      return 'danger';
    default:
      return '';
  }
};

const renderDuration = (data: number[], separator: string) => {
  return data.reduce((prev, cur) => {
    return !prev ? prev + cur : prev + separator + cur;
  }, '');
};

const convertType = (type: string) => {
  switch (type) {
    case SUB_REGION:
      return t('common.sub_region');
    case AIRPORT:
      return t('common.airport');
    case REGION:
      return t('common.region');
    default:
      return '';
  }
};

const openEditForm = (editData: any) => {
  emits('openEditTourModal', editData);
};

const checkMatchMode = (dataField: string) => {
  return !['blackoutDate', 'travelDate', 'quoteDate', 'deleteStatus', 'durations'].includes(dataField);
};

const handleDisplayDate = (listDate: any, separator = '\n') => {
  return listDate?.length ? listDate?.map((item: any) => expTxt(item)).join(separator) : '';
};

const expTxt = (item) => {
  return `${formatDate(item.start, 'dd-LLL-yyyy', true, currentLanguage)} to ${formatDate(item.end, 'dd-LLL-yyyy', true, currentLanguage)}`;
};

const handleShowMessage = (message: string) => {
  return message ?? t('common.labels.success');
};

const rowClass = (data: any) => {
  return [{ 'bg-error': data?.validateMessage }];
};

const sortMessage = () => {
  isSort.value = !isSort.value;
  sortOrder.value = isSort.value ? -1 : 0;
};

const handleDataExport = (listData: any) => {
  return listData.map((data: any) => {
    return {
      ...data,
      durations: renderDuration(data.durations, ','),
      blackoutDate: handleDisplayDate(data.blackoutDate, ','),
      travelDate: handleDisplayDate(data.travelDate, ','),
      quoteDate: handleDisplayDate(data.quoteDate, ',')
    };
  });
};

const showPreviewImport = ({ data, file }: any) => {
  emits('handlePreviewFile', {
    tourGatewayDestList: data,
    file,
    type: 'IMPORT'
  });
};

const handleShowRevertVersion = () => {
  emits('handleShowRevertVersion');
};

// Temporary fix due to issue in PrimeVue 4.2.5 for frozen columns
// https://github.com/primefaces/primevue/issues/6067
// To be removed as soon as we have a fix
const frozenTableTourList = () => {
  const tableTours = dataTableTourRef.value?.$el.querySelector('.p-datatable-thead');
  if (tableTours) {
    const colWidths = tableTours.querySelectorAll('th');
    document.documentElement.style.setProperty('--tour-list-width-1', 0 + 'px');
    document.documentElement.style.setProperty('--tour-list-width-2', -0.3 + colWidths[0].offsetWidth + 'px');
    document.documentElement.style.setProperty('--tour-list-width-3', -0.5 + colWidths[0].offsetWidth + colWidths[1].offsetWidth + 'px');
  }
};

onMounted(() => {
  frozenTableTourList();
});

watch(
  () => props.tourGatewayDestDataDefault,
  (newVal: any) => {
    sheetsData.value = [
      {
        name: 'Sheet 1',
        data: formatDataExport(handleDataExport(newVal), formatShowFieldColumn())
      }
    ];
  },
  { deep: true }
);

defineExpose({
  handleDataExport,
  formatShowFieldColumn,
  sheetColumnStyle
});
</script>
<template>
  <div class="tour-management">
    <div class="gateway-dest-table">
      <DataTable
        editMode="cell"
        ref="dataTableTourRef"
        :value="tourGatewayDestData"
        scrollHeight="900px"
        sortMode="single"
        :sortOrder="sortOrder"
        :sortField="sortField"
        scrollable
        v-model:filters="filtersTour"
        paginator
        :rows="rowsPerPageOptions[0]"
        :rowsPerPageOptions="rowsPerPageOptions"
        :rowClass="rowClass"
        showGridlines
        filterDisplay="menu"
        dataKey="sk"
        :globalFilterFields="globalFilterFields"
        class="tour-combine-table"
      >
        <template #empty>{{ $t('management.tour.list_tour_detail.fare_not_available') }}</template>
        <template #header>
          <div class="flex justify-content-between flex-col sm:flex-row">
            <ActionButtons
              @clearFilter="clearFilter()"
              :exportRole="!isView && authorizations.tour_combination_configuration.export_tour_combination"
              :importRole="!isView && authorizations.tour_combination_configuration.import_tour_combination"
              :revertRole="!isView && authorizations.tour_combination_configuration.revert_tour_combination"
              :sheetsData="sheetsData"
              :sheetColumnStyle="sheetColumnStyle"
              :urlUpload="urlUpload"
              @onApply="(data) => showPreviewImport(data)"
              :fileName="renderNameFileExport('Tour_Gateway/Destination')"
              :importLabel="$t('management.tour.upload_tour_gateway_destination')"
              @showRevertVersion="handleShowRevertVersion()"
            >
            </ActionButtons>
            <span class="p-input-icon-left">
              <i class="pi pi-search" />
              <InputText v-model="filtersTour['global'].value" :placeholder="$t('my_quote.search_keyword')" />
            </span>
          </div>
        </template>
        <TableColumn
          v-for="(column, index) of columns"
          :headerStyle="{ 'min-width': column.field == 'durations' ? '200px' : '' }"
          :show-filter-operator="false"
          :show-add-button="false"
          :key="column.field"
          :field="column.field"
          :frozen="index <= 2"
          :sortable="column.field === 'validateMessage'"
          :showFilterMatchModes="checkMatchMode(column.field)"
        >
          <template #header>
            <span class="flex justify-content-center gap-3 align-content-center w-full">
              {{ column.header }}
            </span>
          </template>
          <template #body="{ data, field }">
            <span v-if="field === 'blackoutDate' || field === 'travelDate' || field === 'quoteDate'">
              {{ handleDisplayDate(data[field]) }}
            </span>
            <span v-else-if="field === 'gateReferenceType' || field === 'destReferenceType'">
              {{ convertType(data[field]) }}
            </span>
            <span v-else-if="field === 'combinationStatus'">
              <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
            </span>
            <span v-else-if="field === 'durations'">
              <div class="flex flex-col">
                <span>{{ renderDuration(data[field], ', ') }}</span>
              </div>
            </span>
            <span v-else-if="field === 'typeInput'">
              <div class="flex flex-col">
                <span> {{ data[field] ? data[field].toUpperCase() : '' }} </span>
              </div>
            </span>
            <span v-else-if="field === 'deleteStatus'">
              <div class="flex flex-col">
                <span> {{ data[field] ? $t('management.tour.deleted') : '' }} </span>
              </div>
            </span>
            <span v-else-if="field === 'validateMessage'">
              <div class="min-w-200">
                <p class="mb-0 break-spaces">{{ handleShowMessage(data[field]) }}</p>
              </div>
            </span>
            <span v-else-if="field === 'action'">
              <div class="flex">
                <Button v-if="authorizations.tour_combination_configuration.edit_tour_combination_manually" icon="pi pi-pencil" class="p-button-text" @click="openEditForm(data)" />
                <Button v-if="authorizations.tour_combination_configuration.delete_tour_combination_manually" icon="pi pi-trash" class="p-button-text" @click="deleteTour(data)" />
              </div>
            </span>
            <span v-else>
              {{ data[field] }}
            </span>
          </template>
          <template #sorticon>
            <i class="ml-2 sort-icon" :class="sortOrder === -1 ? 'pi pi-sort-amount-down-alt' : 'pi pi-sort-amount-up-alt'" @click="sortMessage()"></i>
          </template>
          <template #filter="{ filterModel }" v-if="globalFilterFields.includes(column.field)">
            <Select v-if="column.field === 'combinationStatus'" v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
              <template #option="slotProps">
                <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
              </template>
            </Select>
            <Select
              v-else-if="column.field === 'gateReferenceType' || column.field === 'destReferenceType' || column.field === 'deleteStatus'"
              v-model="filterModel.value"
              optionLabel="name"
              optionValue="value"
              :options="column.field === 'deleteStatus' ? deletedStatuses : referenceTypeList"
              :placeholder="$t('common.select_one')"
              class="p-column-filter"
              showClear
            >
              <template #option="slotProps">
                <Tag v-if="column.field === 'deleteStatus'" :value="slotProps.option.name" :severity="getDeleteSeverity(slotProps.option.value)" />
              </template>
            </Select>
            <InputText v-else v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="column.header" />
          </template>
        </TableColumn>
      </DataTable>
    </div>
  </div>
</template>
